import { inject, ref, defineComponent, watchEffect, } from 'vue';
import classNames from '../_util/classNames';
import PropTypes from '../_util/vue-types';
import CloseOutlined from '@ant-design/icons-vue/CloseOutlined';
import Wave from '../_util/wave';
import { PresetColorTypes, PresetStatusColorTypes, } from '../_util/colors';
import { defaultConfigProvider } from '../config-provider';
import CheckableTag from './CheckableTag';
const PresetColorRegex = new RegExp(`^(${PresetColorTypes.join('|')})(-inverse)?$`);
const PresetStatusColorRegex = new RegExp(`^(${PresetStatusColorTypes.join('|')})$`);
const tagProps = {
    prefixCls: PropTypes.string,
    color: {
        type: String,
    },
    closable: PropTypes.looseBool.def(false),
    closeIcon: PropTypes.VNodeChild,
    visible: PropTypes.looseBool,
    onClose: {
        type: Function,
    },
    icon: PropTypes.VNodeChild,
};
const Tag = defineComponent({
    name: 'ATag',
    emits: ['update:visible', 'close'],
    setup(props, { slots, emit, attrs }) {
        const { getPrefixCls } = inject('configProvider', defaultConfigProvider);
        const visible = ref(true);
        watchEffect(() => {
            if (props.visible !== undefined) {
                visible.value = props.visible;
            }
        });
        const handleCloseClick = (e) => {
            e.stopPropagation();
            emit('update:visible', false);
            emit('close', e);
            if (e.defaultPrevented) {
                return;
            }
            if (props.visible === undefined) {
                visible.value = false;
            }
        };
        const isPresetColor = () => {
            const { color } = props;
            if (!color) {
                return false;
            }
            return PresetColorRegex.test(color) || PresetStatusColorRegex.test(color);
        };
        return () => {
            var _a, _b, _c;
            const { prefixCls: customizePrefixCls, icon = (_a = slots.icon) === null || _a === void 0 ? void 0 : _a.call(slots), color, closeIcon = (_b = slots.closeIcon) === null || _b === void 0 ? void 0 : _b.call(slots), closable = false, } = props;
            const presetColor = isPresetColor();
            const prefixCls = getPrefixCls('tag', customizePrefixCls);
            const renderCloseIcon = () => {
                if (closable) {
                    return closeIcon ? (<div class={`${prefixCls}-close-icon`} onClick={handleCloseClick}>
              {closeIcon}
            </div>) : (<CloseOutlined class={`${prefixCls}-close-icon`} onClick={handleCloseClick}/>);
                }
                return null;
            };
            const tagStyle = {
                backgroundColor: color && !isPresetColor() ? color : undefined,
            };
            const tagClassName = classNames(prefixCls, {
                [`${prefixCls}-${color}`]: presetColor,
                [`${prefixCls}-has-color`]: color && !presetColor,
                [`${prefixCls}-hidden`]: !visible.value,
            });
            const iconNode = icon || null;
            const children = (_c = slots.default) === null || _c === void 0 ? void 0 : _c.call(slots);
            const kids = iconNode ? (<>
          {iconNode}
          <span>{children}</span>
        </>) : (children);
            const isNeedWave = 'onClick' in attrs;
            const tagNode = (<span class={tagClassName} style={tagStyle}>
          {kids}
          {renderCloseIcon()}
        </span>);
            return isNeedWave ? <Wave>{tagNode}</Wave> : tagNode;
        };
    },
});
Tag.props = tagProps;
Tag.CheckableTag = CheckableTag;
Tag.install = function (app) {
    app.component(Tag.name, Tag);
    app.component(CheckableTag.name, CheckableTag);
    return app;
};
export default Tag;
